/**
 * Hook for Acquired Book Performance metrics
 *
 * Fetches metrics showing how an acquired loan book performs:
 * - How many loans have discharged FROM the acquired book
 * - How many were retained (refinanced INTO the primary book)
 * - How many were lost to external competitors
 * - Monthly and yearly trends
 */

import { useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface MonthlyAcquiredBookData {
  month: string;  // YYYY-MM
  discharges: number;
  retained: number;
  retainedConfirmed: number;
  retainedPending: number;
  lost: number;
  retentionRate: number;
  dischargedValueCents: number;
  retainedCommissionCents: number;
  retainedLoanAmountCents: number;
}

export interface YearlyAcquiredBookData {
  year: number;
  discharges: number;
  retained: number;
  retainedConfirmed: number;
  retainedPending: number;
  lost: number;
  retentionRate: number;
  dischargedValueCents: number;
  retainedCommissionCents: number;
  retainedLoanAmountCents: number;
}

export interface AcquiredBookMetrics {
  companyId: string;
  acquiredTemplateId: string;
  acquiredTemplateName: string;
  primaryTemplateId: string;
  primaryTemplateName: string;
  periodStart: string;
  periodEnd: string;

  // Summary totals
  totalDischargesFromBook: number;
  dischargedValueCents: number;
  matchedDischargedLoans: number;  // Discharged loans that were refinanced (for retention rate)
  retainedToPrimary: number;       // New loans created (may be fewer due to consolidations)
  retainedConfirmed: number;
  retainedPending: number;
  retainedCommissionCents: number;           // Upfront commission from ALL retained (confirmed + pending)
  retainedConfirmedCommissionCents: number;  // Upfront commission from CONFIRMED only
  retainedLoanAmountCents: number;
  lostToExternal: number;
  lostValueCents: number;
  retentionRate: number;

  // Trail comparison metrics
  previousAvgMonthlyTrailCents: number;
  newAvgMonthlyTrailCents: number;
  trailDeltaCents: number;

  // Trail book health metrics (first run/month to latest run/month comparison)
  firstRunTrailCents: number;
  latestRunTrailCents: number;
  trailChangePercent: number;
  firstRunMonth: string;
  latestRunMonth: string;

  // Trends
  monthlyTrends: MonthlyAcquiredBookData[];
  yearlySummary: YearlyAcquiredBookData[];
}

export interface UseAcquiredBookMetricsOptions {
  companyId: string;
  acquiredTemplateId: string;
  primaryTemplateId?: string;
  officeId?: string;
  periodStart?: string;
  periodEnd?: string;
  entityCode?: string;
}

export interface UseAcquiredBookMetricsReturn {
  metrics: AcquiredBookMetrics | null;
  isLoading: boolean;
  error: Error | null;
  fetchMetrics: (options: UseAcquiredBookMetricsOptions) => Promise<AcquiredBookMetrics | null>;
  clearMetrics: () => void;
}

/**
 * Hook to fetch acquired book performance metrics
 */
export function useAcquiredBookMetrics(): UseAcquiredBookMetricsReturn {
  const [metrics, setMetrics] = useState<AcquiredBookMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const fetchMetrics = useCallback(async (options: UseAcquiredBookMetricsOptions): Promise<AcquiredBookMetrics | null> => {
    const { companyId, acquiredTemplateId, primaryTemplateId, officeId, periodStart, periodEnd, entityCode } = options;

    if (!companyId || !acquiredTemplateId) {
      setError(new Error('Company ID and Acquired Template ID are required'));
      return null;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const getAcquiredBookMetricsFn = httpsCallable<
        {
          companyId: string;
          acquiredTemplateId: string;
          primaryTemplateId?: string;
          officeId?: string;
          periodStart?: string;
          periodEnd?: string;
          entityCode?: string;
        },
        { success: boolean; metrics: AcquiredBookMetrics }
      >(functions, 'getAcquiredBookMetrics');

      // Build request object, only including defined values
      const requestData: {
        companyId: string;
        acquiredTemplateId: string;
        primaryTemplateId?: string;
        officeId?: string;
        periodStart?: string;
        periodEnd?: string;
        entityCode?: string;
      } = {
        companyId,
        acquiredTemplateId,
      };
      if (primaryTemplateId) requestData.primaryTemplateId = primaryTemplateId;
      if (officeId) requestData.officeId = officeId;
      if (periodStart) requestData.periodStart = periodStart;
      if (periodEnd) requestData.periodEnd = periodEnd;
      if (entityCode) requestData.entityCode = entityCode;

      const result = await getAcquiredBookMetricsFn(requestData);

      // Discard stale response if a newer request was made
      if (currentRequestId !== requestIdRef.current) return null;

      if (result.data.success) {
        setMetrics(result.data.metrics);
        return result.data.metrics;
      } else {
        throw new Error('Failed to fetch acquired book metrics');
      }
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return null;
      const error = err instanceof Error ? err : new Error('Unknown error fetching metrics');
      setError(error);
      console.error('[useAcquiredBookMetrics] Error:', error);
      return null;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const clearMetrics = useCallback(() => {
    setMetrics(null);
    setError(null);
  }, []);

  return {
    metrics,
    isLoading,
    error,
    fetchMetrics,
    clearMetrics,
  };
}
